import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useCart } from '../../contexts/CartContext';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter } from '../ui/sheet';
import { Button } from '../ui/button';
import { Trash2, ShoppingBasket, Calendar, MapPin, Video } from 'lucide-react';

import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useDateLocale } from '../../hooks/useDateLocale';
import { FeatureFlag, useFeatureFlags } from '../../contexts/FeatureFlagsContext';
import { buildApiUrl } from '../../config/apiConfig';
import { giftChrome } from '../../pages/GiftVoucherPage/giftExperience';
import {
  cartItemWalletKind,
  cartItemsSummaryTotal,
  overlayGiftWalletOnCartItems,
  type CheckoutForfait,
} from '../../pages/Checkout/checkoutPayload';
import { getUserCredits } from '../../services/api/proApi';
import { subscribeGiftClaim } from '../../utils/giftVoucherClaim';
import { useFormatServicePrice } from '../../utils/formatServicePrice';
import { checkoutLineEuroPrice } from '../../pages/Checkout/checkoutPriceDisplay';

interface CartDrawerProps {
  isOpen: boolean;
  onClose: () => void;
}

export function CartDrawer({ isOpen, onClose }: CartDrawerProps) {
  const { items, removeItem, itemCount } = useCart();
  const { user } = useAuth();
  const { t, i18n } = useTranslation('booking');
  const { freeLabel } = useFormatServicePrice();
  const chrome = giftChrome(i18n.language);
  const navigate = useNavigate();
  const { intlLocale } = useDateLocale();
  const { isFeatureEnabled } = useFeatureFlags();
  const checkoutOn = isFeatureEnabled(FeatureFlag.CHECKOUT_PAYMENTS);
  const [giftForfaits, setGiftForfaits] = useState<CheckoutForfait[]>([]);
  const [giftCredits, setGiftCredits] = useState<Record<number, number>>({});
  const [giftWalletKnown, setGiftWalletKnown] = useState(false);

  const loadGiftWallet = useCallback(async () => {
    if (!user?.user_id) {
      setGiftForfaits([]);
      setGiftCredits({});
      setGiftWalletKnown(false);
      return;
    }
    const token = localStorage.getItem('token') || '';
    const proIds = Array.from(new Set(items.map(item => item.proId)));
    const nextCredits: Record<number, number> = {};
    await Promise.all(
      proIds.map(async proId => {
        try {
          nextCredits[proId] = await getUserCredits(user.user_id, proId, token);
        } catch {
          /* ignore */
        }
      })
    );
    setGiftCredits(nextCredits);
    try {
      const response = await axios.get(buildApiUrl(`/forfaits?user_id=${user.user_id}`), {
        headers: { Authorization: `Bearer ${token}` },
      });
      setGiftForfaits(response.data.forfaits || []);
      setGiftWalletKnown(true);
    } catch {
      setGiftForfaits([]);
    }
  }, [user?.user_id, items]);

  useEffect(() => {
    if (!isOpen) return;
    void loadGiftWallet();
  }, [isOpen, loadGiftWallet]);

  useEffect(
    () =>
      subscribeGiftClaim(() => {
        if (isOpen) void loadGiftWallet();
      }),
    [isOpen, loadGiftWallet]
  );

  const displayItems = overlayGiftWalletOnCartItems(items, giftForfaits, giftCredits, {
    forfaitsKnown: giftWalletKnown,
    creditsKnown: giftWalletKnown,
  });
  const summary = cartItemsSummaryTotal(displayItems);
  const totalLabel =
    summary.kind === 'forfait'
      ? chrome.includedShort
      : summary.kind === 'credits'
        ? chrome.creditsCount(summary.credits)
        : summary.amount === 0
          ? freeLabel
          : `${summary.amount}€`;

  const handleCheckout = () => {
    if (!checkoutOn) return;
    onClose();
    navigate('/checkout');
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '';
    try {
      // Normalize to midday to prevent UTC to local timezone negative shifts
      const normalizedDate = dateStr.includes('T') ? dateStr : `${dateStr}T12:00:00`;
      const date = new Date(normalizedDate);
      return date.toLocaleDateString(intlLocale, { day: '2-digit', month: 'short' });
    } catch {
      return dateStr;
    }
  };

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent
        side='right'
        className='cart-drawer w-full sm:max-w-md flex flex-col p-0'
        onPointerDownOutside={e => {
          const target = e.target as HTMLElement;
          if (target.closest('.Toastify__toast-container')) {
            e.preventDefault();
          }
        }}
      >
        <SheetHeader className='cart-drawer__header p-5 border-b'>
          <SheetTitle className='flex items-center gap-2'>
            <ShoppingBasket className='w-5 h-5 text-[#a5765c]' />
            {t('cart.title', { count: itemCount })}
          </SheetTitle>
        </SheetHeader>

        <div className='flex-1 overflow-y-auto p-5'>
          {items.length === 0 ? (
            <div className='h-full flex flex-col items-center justify-center text-center space-y-4'>
              <div className='w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center'>
                <ShoppingBasket className='w-8 h-8 text-gray-400' />
              </div>
              <div>
                <p className='text-gray-900 font-semibold'>{t('cart.emptyTitle')}</p>
                <p className='text-gray-500 text-sm'>{t('cart.emptySubtitle')}</p>
              </div>
              <Button onClick={onClose} variant='outline' className='rounded-full'>
                {t('cart.continueBrowsing')}
              </Button>
            </div>
          ) : (
            <div className='space-y-4'>
              {displayItems.map(item => {
                const wallet = cartItemWalletKind(item);
                const sessionDate = item.date || item.session?.date;
                const sessionTime = item.time || item.session?.start_time;
                return (
                <div
                  key={item.cartItemId}
                  className='flex items-center gap-4 p-3 rounded-xl border border-gray-100 bg-white'
                >
                  <div className='flex-1 min-w-0'>
                    <h4 className='font-semibold text-sm text-gray-900 truncate'>
                      {item.serviceName}
                    </h4>
                    <p className='text-xs text-gray-500 truncate'>{item.proName}</p>

                    <div className='mt-1 mb-1 flex flex-col gap-0.5'>
                      {sessionDate && (
                        <div className='flex items-center text-[11px] text-gray-500'>
                          <Calendar className='w-3 h-3 mr-1 flex-shrink-0' />
                          <span className='truncate'>
                            {sessionTime
                              ? t('checkout.dateAtTime', {
                                  date: formatDate(sessionDate),
                                  time: sessionTime,
                                })
                              : formatDate(sessionDate)}
                          </span>
                        </div>
                      )}
                      {(item.isHome || item.isOnline || item.locationName) && (
                        <div className='flex items-center text-[11px] text-gray-500'>
                          {item.isOnline ? (
                            <>
                              <Video className='w-3 h-3 mr-1 flex-shrink-0' />
                              <span className='truncate'>{t('checkout.onlineVisio')}</span>
                            </>
                          ) : (
                            <>
                              <MapPin className='w-3 h-3 mr-1 flex-shrink-0' />
                              <span className='truncate'>
                                {item.isHome ? t('checkout.atHome') : item.locationName}
                              </span>
                            </>
                          )}
                        </div>
                      )}
                    </div>

                    <p className='text-[#a5765c] font-bold text-sm'>
                      {wallet === 'forfait'
                        ? chrome.includedShort
                        : wallet === 'credits'
                          ? chrome.creditsCount(item.creditPrice || 0)
                          : checkoutLineEuroPrice(item.price, freeLabel)}
                    </p>
                  </div>
                  <button
                    onClick={() => removeItem(item.cartItemId)}
                    className='cart-remove p-2 text-gray-400 hover:text-red-500 transition-colors'
                    aria-label={t('cart.removeItem')}
                  >
                    <Trash2 className='w-4 h-4' />
                  </button>
                </div>
                );
              })}
            </div>
          )}
        </div>

        {items.length > 0 && (
          <SheetFooter className='p-5 border-t bg-gray-50'>
            <div className='w-full space-y-4'>
              <div className='flex items-center justify-between'>
                <span className='text-gray-600 font-medium'>{t('cart.total')}</span>
                <span className='text-xl font-bold text-gray-900'>{totalLabel}</span>
              </div>
              <p className='text-xs text-amber-800 font-medium text-center'>
                {t('cart.bookingNotComplete')}
              </p>
              <Button
                onClick={handleCheckout}
                disabled={!checkoutOn}
                className='w-full rounded-full py-6 text-base font-bold'
                style={{
                  background: 'linear-gradient(135deg, #a5765c 0%, #8f6249 100%)',
                }}
              >
                {t('cart.finalizeBooking')}
              </Button>
            </div>
          </SheetFooter>
        )}
      </SheetContent>
    </Sheet>
  );
}
